import * as React from "react"
import { Tooltip as TooltipPrimitive } from "radix-ui"

import { cn } from "@/shadcn/lib/utils"

type TooltipProps = React.ComponentProps<typeof TooltipPrimitive.Root> & {
  children: React.ReactElement
  content: React.ReactNode
  hidden?: boolean
  className?: string
  sideOffset?: number
  side?: "top" | "right" | "bottom" | "left"
}

function TooltipProvider({ delayDuration = 200, ...props }: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return <TooltipPrimitive.Provider data-slot="tooltip-provider" delayDuration={delayDuration} {...props} />
}

function Tooltip({ children, content, hidden, className, sideOffset = 0, side = "top", ...props }: TooltipProps) {
  if (hidden || !content) {
    return children
  }

  return (
    <TooltipPrimitive.Root data-slot="tooltip" disableHoverableContent {...props}>
      <TooltipPrimitive.Trigger
        data-slot="tooltip-trigger"
        asChild
        // Hover-only: prevent Radix from opening the tooltip on focus (which
        // also fires when the trigger regains focus programmatically, e.g. when
        // a menu anchored to it closes). composeEventHandlers skips Radix's
        // open handler once we mark the focus event as default-prevented.
        onFocus={(event) => event.preventDefault()}
      >
        {children}
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          data-slot="tooltip-content"
          sideOffset={sideOffset}
          side={side}
          className={cn(
            "z-50 inline-flex w-fit max-w-xs origin-(--radix-tooltip-content-transform-origin) items-center gap-1.5 rounded-md bg-tooltip px-3 py-1.5 text-[13px] font-semibold text-tooltip-foreground has-data-[slot=kbd]:pr-1.5 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 **:data-[slot=kbd]:relative **:data-[slot=kbd]:isolate **:data-[slot=kbd]:z-50 **:data-[slot=kbd]:rounded-sm data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
            className
          )}
        >
          {content}
          <TooltipPrimitive.Arrow className="z-50 size-2.5 translate-y-[calc(-50%-2px)] rotate-45 rounded-[2px] bg-tooltip fill-tooltip" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}

export { Tooltip, TooltipProvider }
